import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { CompanyMultiPicker, type PickedCompany } from '../components/CompanyMultiPicker';
import { api, newIdempotencyKey } from '../lib/api';
import { useStoreChoice } from '../lib/store-choice';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  BulkOperation,
  BulkPreview,
  Country,
  CustomerGroup,
  Discount,
  PriceList,
  PriceListFxResult,
  Store,
  TaxClass,
  TaxCountryRate,
} from '../lib/types';
import { useCatalogRefs } from './products';
import { useBulletinDate, usePairLabel } from './fx';

export function useGroups() {
  return useQuery({
    queryKey: ['customer-groups'],
    queryFn: () => api.get<CustomerGroup[]>('/v1/admin/customer-groups'),
  });
}
export function usePriceLists() {
  return useQuery({
    queryKey: ['price-lists'],
    queryFn: () => api.get<PriceList[]>('/v1/admin/price-lists'),
  });
}

/** ISO → datetime-local girdisi (tarayıcı saatine göre). */
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const localInputToIso = (v: string) => (v ? new Date(v).toISOString() : null);

/** Tarihli (kampanya) listenin şu anki durumu. */
function campaignState(l: Pick<PriceList, 'startsAt' | 'endsAt'>): 'upcoming' | 'live' | 'ended' | null {
  if (!l.startsAt && !l.endsAt) return null;
  const now = Date.now();
  if (l.startsAt && Date.parse(l.startsAt) > now) return 'upcoming';
  if (l.endsAt && Date.parse(l.endsAt) < now) return 'ended';
  return 'live';
}

/** "20" / "%20" / "7,5" → baz puan. */
export function percentToBps(input: string): number | null {
  const m = /^\s*[-+]?%?\s*(\d{1,4})(?:[.,](\d{1,2}))?\s*%?\s*$/.exec(input);
  if (!m) return null;
  const sign = input.trim().startsWith('-') ? -1 : 1;
  return sign * (Number(m[1]) * 100 + Number((m[2] ?? '0').padEnd(2, '0')));
}

export function PricingPage() {
  const t = useT();
  const { can } = usePermissions();
  return (
    <PageFrame helpKey="page.pricing">
      <GroupsSection canWrite={can('pricing.price.write')} />
      <ListsSection canWrite={can('pricing.price.write')} />
      <TaxSection canWrite={can('catalog.product.write')} canRead={can('catalog.product.read')} />
      {can('pricing.price.write') && <BulkSection />}
      <p className="text-sm text-ink-3">{t('admin.pricing.referencePriceNote')}</p>
    </PageFrame>
  );
}

function GroupsSection({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const groups = useGroups();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', code: '', kind: 'dealer' as 'retail' | 'dealer' });
  const [editing, setEditing] = useState<CustomerGroup | null>(null);
  const money = useMoney();
  const create = useApiMutation(() => api.post('/v1/admin/customer-groups', f), {
    invalidate: [['customer-groups']],
    success: t('common.saved'),
    onSuccess: () => {
      setOpen(false);
      setF({ name: '', code: '', kind: 'dealer' });
    },
    silentFieldErrors: true,
  });
  return (
    <Section
      title={t('admin.pricing.groups')}
      description={t('admin.pricing.groupsLead')}
      action={
        canWrite && (
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.pricing.addGroup')}
          </Button>
        )
      }
    >
      {groups.data?.length ? (
        <ul className="divide-y divide-line rounded-md border border-line">
          {groups.data.map((g) => (
            <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span>
                <span className="font-medium">{g.name}</span>{' '}
                <span className="text-sm text-ink-3">({g.code})</span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                {g.kind === 'dealer' && (
                  <span className="text-sm text-ink-2">
                    {g.minOrderMinor
                      ? t('admin.pricing.minOrderShort', {
                          amount: money(g.minOrderMinor, g.minOrderCurrency),
                        })
                      : t('admin.pricing.noMinOrder')}
                  </span>
                )}
                <Badge tone={g.kind === 'dealer' ? 'pine' : 'neutral'}>
                  {t(`admin.pricing.kinds.${g.kind}`)}
                </Badge>
                {canWrite && g.kind === 'dealer' && (
                  <span className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(g)}>
                      {t('admin.pricing.editMinOrder')}
                    </Button>
                    <HelpTip k="pricing.group_min_order" />
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={t('admin.pricing.noGroups')} description={t('admin.pricing.noGroupsLead')} />
      )}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.pricing.addGroup')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('admin.pricing.groupName')} required error={fieldError(create.error, 'name')}>
            {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
          </Field>
          <Field
            label={t('admin.pricing.groupCode')}
            required
            error={
              fieldError(create.error, 'code') ??
              (create.error?.code === 'code_taken' ? create.error.title : undefined)
            }
            description={t('admin.pricing.groupCodeHint')}
          >
            {(p) => <Input {...p} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />}
          </Field>
          <Field label={t('admin.pricing.groupKind')} help={<HelpTip k="pricing.group_kind" />}>
            {(p) => (
              <Select
                {...p}
                value={f.kind}
                onChange={(e) => setF({ ...f, kind: e.target.value as 'retail' | 'dealer' })}
              >
                <option value="dealer">{t('admin.pricing.kinds.dealer')}</option>
                <option value="retail">{t('admin.pricing.kinds.retail')}</option>
              </Select>
            )}
          </Field>
        </div>
      </Dialog>
      {editing && <GroupMinOrderDialog group={editing} onClose={() => setEditing(null)} />}
    </Section>
  );
}

/** Bayi grubunun minimum sipariş tutarı (F7-05). */
function GroupMinOrderDialog({ group, onClose }: { group: CustomerGroup; onClose: () => void }) {
  const t = useT();
  const [amount, setAmount] = useState(minorToInput(group.minOrderMinor));
  const minor = toMinor(amount, group.minOrderCurrency);
  const invalid = amount.trim() !== '' && (minor === null || minor < 0);
  const save = useApiMutation(
    () =>
      api.patch(`/v1/admin/customer-groups/${group.id}`, {
        minOrderMinor: amount.trim() ? minor : null,
        version: group.version,
      }),
    { invalidate: [['customer-groups']], success: t('common.saved'), onSuccess: onClose },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.pricing.minOrderTitle', { name: group.name })}
      description={t('admin.pricing.minOrderLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={invalid}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.pricing.minOrder', { currency: group.minOrderCurrency })}
        optionalLabel={t('admin.pricing.minOrderHint')}
        help={<HelpTip k="pricing.group_min_order" />}
        error={invalid ? t('admin.ledger.amountInvalid') : undefined}
      >
        {(p) => (
          <Input {...p} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

function ListsSection({ canWrite }: { canWrite: boolean }) {
  const t = useT();
  const lists = usePriceLists();
  const groups = useGroups();
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  // Yeni fiyat listesi varsayılan olarak çalışılan mağazaya (F5B-21).
  const working = useStoreChoice().storeId;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    storeId: '',
    marketId: '',
    name: '',
    customerGroupId: '',
    currency: 'TRY',
    taxInclusive: true,
    priority: '0',
    startsAt: '',
    endsAt: '',
  });
  const { formatDate } = useI18n();
  const [datesOf, setDatesOf] = useState<PriceList | null>(null);
  const [fxOf, setFxOf] = useState<PriceList | null>(null);
  const nameOf = (id: string) => lists.data?.find((x) => x.id === id)?.name ?? '—';
  const storeMarkets = stores.data?.find((s) => s.id === (f.storeId || working))?.markets ?? [];
  const create = useApiMutation(
    () =>
      api.post('/v1/admin/price-lists', {
        storeId: f.storeId || working || stores.data?.[0]?.id,
        marketId: f.marketId || null,
        name: f.name,
        customerGroupId: f.customerGroupId || null,
        currency: f.currency,
        taxInclusive: f.taxInclusive,
        priority: Number(f.priority) || 0,
        status: 'active',
        startsAt: localInputToIso(f.startsAt),
        endsAt: localInputToIso(f.endsAt),
      }),
    {
      invalidate: [['price-lists']],
      success: t('common.saved'),
      onSuccess: () => setOpen(false),
      silentFieldErrors: true,
    },
  );
  const toggle = useApiMutation(
    (l: PriceList) =>
      api.patch(
        `/v1/admin/price-lists/${l.id}`,
        { status: l.status === 'active' ? 'inactive' : 'active' },
        { ifMatch: l.version },
      ),
    { invalidate: [['price-lists']], success: t('common.saved') },
  );
  return (
    <Section
      title={t('admin.pricing.lists')}
      description={t('admin.pricing.listsLead')}
      action={
        canWrite && (
          <Button size="sm" onClick={() => setOpen(true)} disabled={!stores.data?.length}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.pricing.addList')}
          </Button>
        )
      }
    >
      {lists.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : lists.data?.length ? (
        <ul className="divide-y divide-line rounded-md border border-line">
          {lists.data.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
              <div className="min-w-[16rem] flex-1">
                <p className="font-medium">{l.name}</p>
                <p className="text-sm text-ink-3">
                  {l.storeName} · {l.currency} ·{' '}
                  {l.marketName ? `${t('admin.pricing.forMarket', { market: l.marketName })} · ` : ''}
                  {l.taxInclusive ? t('admin.pricing.taxIncl') : t('admin.pricing.taxExcl')} ·{' '}
                  {l.groupName
                    ? t('admin.pricing.forGroup', { group: l.groupName })
                    : t('admin.pricing.baseList')}{' '}
                  · {t('admin.pricing.priceCount', { count: l.priceCount })}
                </p>
              </div>
              {campaignState(l) && (
                <span className="inline-flex flex-wrap items-center gap-1 text-sm text-ink-2">
                  <Badge tone={campaignState(l) === 'live' ? 'amber' : 'neutral'}>
                    {t('admin.pricing.campaign')} · {t(`admin.pricing.campaignStates.${campaignState(l)}`)}
                  </Badge>
                  {t('admin.pricing.validity', {
                    from: l.startsAt ? formatDate(l.startsAt, true) : t('admin.pricing.noLimit'),
                    to: l.endsAt ? formatDate(l.endsAt, true) : t('admin.pricing.noLimit'),
                  })}
                </span>
              )}
              {l.fx && <Badge tone="pine">{t('admin.fx.badge', { base: nameOf(l.fx.baseListId) })}</Badge>}
              <Badge tone={l.status === 'active' ? 'moss' : 'slate'}>
                {t(`admin.pricing.listStatus.${l.status}`)}
              </Badge>
              {canWrite && (
                <span className="inline-flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setFxOf(l)}>
                    {t('admin.fx.deriveButton')}
                  </Button>
                  <HelpTip k="pricing.fx" />
                </span>
              )}
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => setDatesOf(l)}>
                  {t('admin.pricing.editDates')}
                </Button>
              )}
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => toggle.mutate(l)}>
                  {l.status === 'active' ? t('admin.pricing.deactivate') : t('admin.pricing.activate')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={t('admin.pricing.noLists')} description={t('admin.pricing.noListsLead')} />
      )}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.pricing.addList')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('admin.pricing.listName')} required error={fieldError(create.error, 'name')}>
            {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
          </Field>
          <Field label={t('admin.pricing.store')}>
            {(p) => (
              <Select
                {...p}
                value={f.storeId || working}
                onChange={(e) => setF({ ...f, storeId: e.target.value, marketId: '' })}
              >
                {stores.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.pricing.listGroup')} help={<HelpTip k="pricing.list_group" />}>
            {(p) => (
              <Select
                {...p}
                value={f.customerGroupId}
                onChange={(e) => setF({ ...f, customerGroupId: e.target.value })}
              >
                <option value="">{t('admin.pricing.baseListOption')}</option>
                {groups.data?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.pricing.market')}
            help={<HelpTip k="pricing.list_market" />}
            error={fieldError(create.error, 'marketId')}
          >
            {(p) => (
              <Select
                {...p}
                value={f.marketId}
                onChange={(e) => {
                  const m = storeMarkets.find((x) => x.id === e.target.value);
                  setF({ ...f, marketId: e.target.value, currency: m?.currency ?? f.currency });
                }}
              >
                <option value="">{t('admin.pricing.allMarkets')}</option>
                {storeMarkets.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.countryCode} · {m.currency})
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.pricing.currency')}>
            {(p) => (
              <Select
                {...p}
                value={f.currency}
                disabled={!!f.marketId}
                onChange={(e) => setF({ ...f, currency: e.target.value })}
              >
                {['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="flex items-center gap-3">
            <Switch
              checked={f.taxInclusive}
              onCheckedChange={(v) => setF({ ...f, taxInclusive: v })}
              label={t('admin.pricing.taxInclusive')}
            />
            <span className="text-sm">{t('admin.pricing.taxInclusive')}</span>
            <HelpTip k="pricing.tax_inclusive" />
          </div>
          <Field label={t('admin.pricing.priority')} help={<HelpTip k="pricing.priority" />}>
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={f.priority}
                onChange={(e) => setF({ ...f, priority: e.target.value })}
              />
            )}
          </Field>
          <DateRangeFields
            value={{ startsAt: f.startsAt, endsAt: f.endsAt }}
            onChange={(v) => setF({ ...f, ...v })}
            error={fieldError(create.error, 'endsAt')}
          />
        </div>
      </Dialog>
      {datesOf && <ListDatesDialog list={datesOf} onClose={() => setDatesOf(null)} />}
      {fxOf && <FxListDialog list={fxOf} lists={lists.data ?? []} onClose={() => setFxOf(null)} />}
    </Section>
  );
}

const FX_RATE_TYPES = ['forex_buying', 'forex_selling', 'banknote_buying', 'banknote_selling'] as const;
const ENDINGS = ['none', 'whole', 'end_90', 'end_99'] as const;

/**
 * Kurdan türetme (F7-11): listenin fiyatları başka para birimindeki elle fiyatlı temel listeden TCMB kuruyla
 * hesaplanır. Varsayılan kur türü: TL'den dövize döviz alış, dövizden TL'ye döviz satış.
 */
function FxListDialog({
  list,
  lists,
  onClose,
}: {
  list: PriceList;
  lists: PriceList[];
  onClose: () => void;
}) {
  const t = useT();
  const pair = usePairLabel();
  const day = useBulletinDate();
  const bases = lists.filter((x) => x.id !== list.id && !x.fx && x.currency !== list.currency);
  const initialBase = list.fx?.baseListId ?? bases[0]?.id ?? '';
  const defaultType = (baseId: string) =>
    lists.find((x) => x.id === baseId)?.currency === 'TRY' ? 'forex_buying' : 'forex_selling';
  const [f, setF] = useState({
    baseListId: initialBase,
    rateType: (list.fx?.rateType ?? defaultType(initialBase)) as (typeof FX_RATE_TYPES)[number],
    markup: list.fx ? bpsLabel(list.fx.markupBps).replace('%', '') : '0',
    ending: (list.fx?.ending ?? 'none') as (typeof ENDINGS)[number],
    auto: list.fx?.auto ?? true,
  });
  const markupBps = percentToBps(f.markup);
  const done = (r: PriceListFxResult) => {
    if (!r.recompute) toast.success(t('admin.fx.cleared'));
    else if (r.recompute.ok) toast.success(t('admin.fx.saved', { count: r.recompute.changed }));
    else
      toast.warning(
        t('admin.fx.savedNoRate', { reason: t(`errors.${r.recompute.code}`, r.recompute.params) }),
      );
    onClose();
  };
  const save = useApiMutation(
    () =>
      api.put<PriceListFxResult>(
        `/v1/admin/price-lists/${list.id}/fx`,
        {
          fx: {
            baseListId: f.baseListId,
            rateType: f.rateType,
            markupBps: markupBps ?? 0,
            ending: f.ending,
            auto: f.auto,
          },
        },
        { ifMatch: list.version },
      ),
    { invalidate: [['price-lists'], ['fx']], onSuccess: done, silentFieldErrors: true },
  );
  const clear = useApiMutation(
    () =>
      api.put<PriceListFxResult>(
        `/v1/admin/price-lists/${list.id}/fx`,
        { fx: null },
        { ifMatch: list.version },
      ),
    { invalidate: [['price-lists'], ['fx']], onSuccess: done },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.fx.deriveTitle', { name: list.name })}
      description={t('admin.fx.deriveLead', { currency: list.currency })}
      closeLabel={t('common.close')}
      footer={
        <>
          {list.fx && (
            <span className="me-auto inline-flex items-center gap-1">
              <ConfirmButton
                message={t('admin.fx.confirmClear')}
                onConfirm={() => clear.mutateAsync(undefined)}
              >
                {t('admin.fx.clear')}
              </ConfirmButton>
              <HelpTip k="pricing.fx_clear" />
            </span>
          )}
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!f.baseListId || markupBps === null}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {list.fxApplied && (
          <p className="text-sm text-ink-2" role="status">
            {t('admin.fx.lastApplied', {
              date: day(list.fxApplied.bulletinDate),
              pair: [list.fxApplied.from, list.fxApplied.to]
                .filter((s) => s.currency !== 'TRY')
                .map(pair)
                .join(' · '),
              rateType: t(`admin.fx.rateTypes.${list.fxApplied.rateType}`),
            })}
          </p>
        )}
        {bases.length ? (
          <>
            <Field
              label={t('admin.fx.baseList')}
              help={<HelpTip k="pricing.fx_base" />}
              description={t('admin.fx.baseListHint')}
              error={fieldError(save.error, 'baseListId')}
            >
              {(p) => (
                <Select
                  {...p}
                  value={f.baseListId}
                  onChange={(e) =>
                    setF({ ...f, baseListId: e.target.value, rateType: defaultType(e.target.value) })
                  }
                >
                  {bases.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.storeName} · {b.currency})
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.fx.rateType')} help={<HelpTip k="pricing.fx_rate_type" />}>
              {(p) => (
                <Select
                  {...p}
                  value={f.rateType}
                  onChange={(e) => setF({ ...f, rateType: e.target.value as (typeof FX_RATE_TYPES)[number] })}
                >
                  {FX_RATE_TYPES.map((r) => (
                    <option key={r} value={r}>
                      {t(`admin.fx.rateTypes.${r}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t('admin.fx.markup')}
              help={<HelpTip k="pricing.fx_markup" />}
              description={t('admin.fx.markupHint')}
              error={markupBps === null ? t('admin.fx.markupInvalid') : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  inputMode="decimal"
                  value={f.markup}
                  onChange={(e) => setF({ ...f, markup: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('admin.pricing.ending')} help={<HelpTip k="pricing.fx_ending" />}>
              {(p) => (
                <Select
                  {...p}
                  value={f.ending}
                  onChange={(e) => setF({ ...f, ending: e.target.value as (typeof ENDINGS)[number] })}
                >
                  {ENDINGS.map((e) => (
                    <option key={e} value={e}>
                      {t(`admin.pricing.endings.${e}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="flex items-center gap-3">
              <Switch
                checked={f.auto}
                onCheckedChange={(v) => setF({ ...f, auto: v })}
                label={t('admin.fx.autoLabel')}
              />
              <span className="text-sm">{t('admin.fx.autoLabel')}</span>
              <HelpTip k="pricing.fx_auto" />
            </div>
          </>
        ) : (
          <p className="text-sm text-ink-2">{t('admin.fx.noBaseOptions')}</p>
        )}
      </div>
    </Dialog>
  );
}

function DateRangeFields({
  value,
  onChange,
  error,
}: {
  value: { startsAt: string; endsAt: string };
  onChange: (v: { startsAt: string; endsAt: string }) => void;
  error?: string;
}) {
  const t = useT();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field
        label={t('admin.pricing.startsAt')}
        optionalLabel={t('common.optional')}
        help={<HelpTip k="pricing.campaign_dates" />}
      >
        {(p) => (
          <Input
            {...p}
            type="datetime-local"
            value={value.startsAt}
            onChange={(e) => onChange({ ...value, startsAt: e.target.value })}
          />
        )}
      </Field>
      <Field label={t('admin.pricing.endsAt')} optionalLabel={t('common.optional')} error={error}>
        {(p) => (
          <Input
            {...p}
            type="datetime-local"
            value={value.endsAt}
            onChange={(e) => onChange({ ...value, endsAt: e.target.value })}
          />
        )}
      </Field>
      <p className="text-sm text-ink-3 sm:col-span-2">{t('admin.pricing.datesHint')}</p>
    </div>
  );
}

/** Var olan listenin geçerlilik tarihleri (kampanya listesi, F5B-11). */
function ListDatesDialog({ list, onClose }: { list: PriceList; onClose: () => void }) {
  const t = useT();
  const [v, setV] = useState({
    startsAt: isoToLocalInput(list.startsAt),
    endsAt: isoToLocalInput(list.endsAt),
  });
  const save = useApiMutation(
    () =>
      api.patch(
        `/v1/admin/price-lists/${list.id}`,
        { startsAt: localInputToIso(v.startsAt), endsAt: localInputToIso(v.endsAt) },
        { ifMatch: list.version },
      ),
    {
      invalidate: [['price-lists']],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.pricing.editDates')}
      description={list.name}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <DateRangeFields value={v} onChange={setV} error={fieldError(save.error, 'endsAt')} />
    </Dialog>
  );
}

function TaxSection({ canWrite, canRead }: { canWrite: boolean; canRead: boolean }) {
  const t = useT();
  const taxes = useQuery({
    queryKey: ['tax-classes'],
    queryFn: () => api.get<TaxClass[]>('/v1/admin/tax-classes'),
    enabled: canRead,
  });
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const create = useApiMutation(
    () =>
      api.post('/v1/admin/tax-classes', {
        name,
        rateBps: percentToBps(rate) ?? -1,
        countryCode: 'TR',
        isDefault: !taxes.data?.length,
      }),
    {
      invalidate: [['tax-classes']],
      success: t('common.saved'),
      onSuccess: () => {
        setName('');
        setRate('');
      },
    },
  );
  if (!canRead) return null;
  return (
    <Section
      title={t('admin.pricing.taxes')}
      description={t('admin.pricing.taxesLead')}
      action={<HelpTip k="pricing.tax_class" />}
    >
      <ul className="mb-3 divide-y divide-line rounded-md border border-line">
        {taxes.data?.map((x) => (
          <li key={x.id} className="flex items-center justify-between px-4 py-2.5">
            <span>
              {x.name} <span className="text-ink-3">{bpsLabel(x.rateBps)}</span>
            </span>
            {x.isDefault && <Badge tone="pine">{t('admin.pricing.defaultTax')}</Badge>}
          </li>
        ))}
        {!taxes.data?.length && (
          <li className="px-4 py-2.5 text-sm text-ink-3">{t('admin.pricing.noTaxes')}</li>
        )}
      </ul>
      {canWrite && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('admin.pricing.taxName')}>
            {(p) => (
              <Input
                {...p}
                value={name}
                placeholder={t('admin.pricing.taxNamePlaceholder')}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.pricing.taxRate')}>
            {(p) => (
              <Input
                {...p}
                className="w-24"
                inputMode="decimal"
                value={rate}
                placeholder="%20"
                onChange={(e) => setRate(e.target.value)}
              />
            )}
          </Field>
          <Button
            loading={create.isPending}
            onClick={() => create.mutate(undefined)}
            disabled={!name || percentToBps(rate) === null}
          >
            {t('admin.pricing.addTax')}
          </Button>
        </div>
      )}
      {!!taxes.data?.length && <TaxCountryRates classes={taxes.data} canWrite={canWrite} />}
    </Section>
  );
}

/**
 * KDV sınıflarının ülke oranları (F7-13, ADR-118): vergi kipi "varış ülkesi" olan pazarda ürünün sınıfının o ülke
 * oranı uygulanır; oran yoksa ürün o pazarda satılamaz.
 */
function TaxCountryRates({ classes, canWrite }: { classes: TaxClass[]; canWrite: boolean }) {
  const t = useT();
  const { locale } = useI18n();
  const rates = useQuery({
    queryKey: ['tax-country-rates'],
    queryFn: () => api.get<TaxCountryRate[]>('/v1/admin/tax-classes/country-rates'),
  });
  const countries = useQuery({
    queryKey: ['countries'],
    queryFn: () => api.get<Country[]>('/v1/admin/countries'),
    staleTime: 3_600_000,
    enabled: canWrite,
  });
  const [f, setF] = useState({ taxClassId: '', countryCode: '', rate: '' });
  const classId = f.taxClassId || classes[0]?.id || '';
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/tax-classes/country-rates', {
        taxClassId: classId,
        countryCode: f.countryCode,
        rateBps: percentToBps(f.rate) ?? -1,
      }),
    {
      invalidate: [['tax-country-rates']],
      success: t('common.saved'),
      onSuccess: () => setF({ ...f, rate: '' }),
    },
  );
  const del = useApiMutation(
    (r: TaxCountryRate) => api.del(`/v1/admin/tax-classes/${r.taxClassId}/country-rates/${r.countryCode}`),
    { invalidate: [['tax-country-rates']], success: t('common.deleted') },
  );
  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? '—';
  return (
    <div className="mt-6 space-y-3">
      <div className="flex items-center gap-1">
        <h3 className="font-medium">{t('admin.pricing.countryRates')}</h3>
        <HelpTip k="pricing.tax_country_rate" />
      </div>
      <p className="text-sm text-ink-2">{t('admin.pricing.countryRatesLead')}</p>
      <ul
        className="divide-y divide-line rounded-md border border-line"
        aria-label={t('admin.pricing.countryRates')}
      >
        {rates.data?.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5">
            <span className="flex-1">
              {r.countryName ?? r.countryCode} <span className="text-ink-3">({r.countryCode})</span>
            </span>
            <span className="text-sm text-ink-2">{className(r.taxClassId)}</span>
            <span className="tabular font-medium">{bpsLabel(r.rateBps)}</span>
            {canWrite && (
              <ConfirmButton
                message={t('admin.pricing.countryRateDelete', {
                  country: r.countryName ?? r.countryCode,
                  name: className(r.taxClassId),
                })}
                onConfirm={() => del.mutateAsync(r)}
              >
                {t('common.delete')}
              </ConfirmButton>
            )}
          </li>
        ))}
        {rates.data && !rates.data.length && (
          <li className="px-4 py-2.5 text-sm text-ink-3">{t('admin.pricing.noCountryRates')}</li>
        )}
      </ul>
      {canWrite && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('admin.pricing.countryRateClass')}>
            {(p) => (
              <Select {...p} value={classId} onChange={(e) => setF({ ...f, taxClassId: e.target.value })}>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.pricing.countryRateCountry')}>
            {(p) => (
              <Select
                {...p}
                value={f.countryCode}
                onChange={(e) => setF({ ...f, countryCode: e.target.value })}
              >
                <option value="">—</option>
                {(countries.data ?? [])
                  .filter((c) => c.code !== 'TR')
                  .map((c) => (
                    <option key={c.code} value={c.code}>
                      {locale === 'en' ? c.nameEn : c.nameTr} ({c.code})
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label={t('admin.pricing.taxRate')}>
            {(p) => (
              <Input
                {...p}
                className="w-24"
                inputMode="decimal"
                value={f.rate}
                placeholder="%19"
                onChange={(e) => setF({ ...f, rate: e.target.value })}
              />
            )}
          </Field>
          <Button
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
            disabled={!classId || !f.countryCode || percentToBps(f.rate) === null}
          >
            {t('admin.pricing.addCountryRate')}
          </Button>
        </div>
      )}
    </div>
  );
}

function BulkSection() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const lists = usePriceLists();
  const { brands, categories } = useCatalogRefs();
  const history = useQuery({
    queryKey: ['bulk-prices'],
    queryFn: () => api.get<BulkOperation[]>('/v1/admin/bulk-prices'),
  });
  const canCost = can('pricing.cost.read');
  const [f, setF] = useState({
    priceListId: '',
    type: 'percent' as 'percent' | 'fixed' | 'from_list' | 'from_cost' | 'target_margin' | 'fx',
    value: '',
    sourcePriceListId: '',
    rate: '',
    brandId: '',
    categoryId: '',
    ending: 'none',
    minMargin: '',
    minMarginMode: 'warn' as 'warn' | 'block',
  });
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const list = lists.data?.find((l) => l.id === (f.priceListId || lists.data?.[0]?.id));
  const run = useApiMutation(
    () => {
      const bps = percentToBps(f.value) ?? 0;
      const operation =
        f.type === 'fixed'
          ? { type: 'fixed', amountMinor: toMinor(f.value, list?.currency ?? 'TRY') ?? 0 }
          : f.type === 'from_list'
            ? { type: 'from_list', sourcePriceListId: f.sourcePriceListId, bps }
            : f.type === 'from_cost'
              ? { type: 'from_cost', markupBps: percentToBps(f.value) ?? -1 }
              : f.type === 'target_margin'
                ? { type: 'target_margin', marginBps: percentToBps(f.value) ?? -1 }
                : f.type === 'fx'
                  ? { type: 'fx', sourcePriceListId: f.sourcePriceListId, rate: f.rate.trim(), bps }
                  : { type: 'percent', bps };
      return api.post<BulkPreview>('/v1/admin/bulk-prices/preview', {
        priceListId: list?.id,
        filter: {
          ...(f.brandId ? { brandId: f.brandId } : {}),
          ...(f.categoryId ? { categoryId: f.categoryId } : {}),
        },
        operation,
        ending: f.ending,
        ...(canCost && f.minMargin.trim()
          ? { minMarginBps: percentToBps(f.minMargin) ?? -100_000, minMarginMode: f.minMarginMode }
          : {}),
      });
    },
    { onSuccess: (r) => setPreview(r), silentFieldErrors: true },
  );
  const valueLabel =
    f.type === 'fixed'
      ? t('admin.pricing.amount')
      : f.type === 'from_cost'
        ? t('admin.pricing.markup')
        : f.type === 'target_margin'
          ? t('admin.pricing.targetMargin')
          : f.type === 'fx'
            ? t('admin.pricing.fxAdjust')
            : t('admin.pricing.percent');
  const valueHint =
    f.type === 'from_cost'
      ? t('admin.pricing.markupHint')
      : f.type === 'target_margin'
        ? t('admin.pricing.targetMarginHint')
        : t('admin.pricing.valueHint');
  const ready =
    !!list &&
    (f.type === 'fx' ? !!f.rate.trim() && !!f.sourcePriceListId : !!f.value.trim()) &&
    (f.type !== 'from_list' || !!f.sourcePriceListId);
  const showMargin = !!preview?.sample.some((s) => s.marginBps !== null);
  const apply = useApiMutation(
    (id: string) =>
      api.post(`/v1/admin/bulk-prices/${id}/apply`, undefined, { idempotencyKey: newIdempotencyKey() }),
    {
      invalidate: [['bulk-prices'], ['product-prices']],
      success: t('admin.pricing.bulkApplied'),
      onSuccess: () => setPreview(null),
    },
  );
  const revert = useApiMutation(
    (id: string) =>
      api.post(`/v1/admin/bulk-prices/${id}/revert`, undefined, { idempotencyKey: newIdempotencyKey() }),
    { invalidate: [['bulk-prices'], ['product-prices']], success: t('admin.pricing.bulkReverted') },
  );
  return (
    <Section
      title={t('admin.pricing.bulk')}
      description={t('admin.pricing.bulkLead')}
      action={<HelpTip k="pricing.bulk_preview" />}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t('admin.pricing.targetList')}>
          {(p) => (
            <Select
              {...p}
              value={f.priceListId || list?.id || ''}
              onChange={(e) => setF({ ...f, priceListId: e.target.value })}
            >
              {lists.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.pricing.operation')}>
          {(p) => (
            <Select
              {...p}
              value={f.type}
              onChange={(e) => setF({ ...f, type: e.target.value as typeof f.type })}
            >
              <option value="percent">{t('admin.pricing.ops.percent')}</option>
              <option value="fixed">{t('admin.pricing.ops.fixed')}</option>
              <option value="from_list">{t('admin.pricing.ops.from_list')}</option>
              {canCost && <option value="from_cost">{t('admin.pricing.ops.from_cost')}</option>}
              {canCost && <option value="target_margin">{t('admin.pricing.ops.target_margin')}</option>}
              <option value="fx">{t('admin.pricing.ops.fx')}</option>
            </Select>
          )}
        </Field>
        <Field
          label={valueLabel}
          description={valueHint}
          optionalLabel={f.type === 'fx' ? t('common.optional') : undefined}
          help={
            f.type === 'from_cost' || f.type === 'target_margin' ? (
              <HelpTip k="pricing.cost_based" />
            ) : undefined
          }
          error={fieldError(run.error, 'operation.markupBps') ?? fieldError(run.error, 'operation.marginBps')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={f.value}
              onChange={(e) => setF({ ...f, value: e.target.value })}
            />
          )}
        </Field>
        {(f.type === 'from_list' || f.type === 'fx') && (
          <Field
            label={t('admin.pricing.sourceList')}
            error={fieldError(run.error, 'operation.sourcePriceListId')}
          >
            {(p) => (
              <Select
                {...p}
                value={f.sourcePriceListId}
                onChange={(e) => setF({ ...f, sourcePriceListId: e.target.value })}
              >
                <option value="">—</option>
                {lists.data
                  ?.filter(
                    (l) =>
                      l.id !== list?.id &&
                      (f.type === 'fx' ? l.currency !== list?.currency : l.currency === list?.currency),
                  )
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.currency})
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        )}
        {f.type === 'fx' && (
          <Field
            label={t('admin.pricing.fxRate')}
            required
            description={t('admin.pricing.fxRateHint')}
            help={<HelpTip k="pricing.fx_rate" />}
            error={fieldError(run.error, 'operation.rate')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={f.rate}
                onChange={(e) => setF({ ...f, rate: e.target.value })}
              />
            )}
          </Field>
        )}
        <Field label={t('admin.pricing.filterBrand')}>
          {(p) => (
            <Select {...p} value={f.brandId} onChange={(e) => setF({ ...f, brandId: e.target.value })}>
              <option value="">{t('common.all')}</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.pricing.filterCategory')}>
          {(p) => (
            <Select {...p} value={f.categoryId} onChange={(e) => setF({ ...f, categoryId: e.target.value })}>
              <option value="">{t('common.all')}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.pricing.ending')} help={<HelpTip k="pricing.price_ending" />}>
          {(p) => (
            <Select {...p} value={f.ending} onChange={(e) => setF({ ...f, ending: e.target.value })}>
              {['none', 'whole', 'end_90', 'end_99'].map((x) => (
                <option key={x} value={x}>
                  {t(`admin.pricing.endings.${x}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {canCost && (
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field
            label={t('admin.pricing.minMargin')}
            optionalLabel={t('common.optional')}
            description={t('admin.pricing.minMarginHint')}
            help={<HelpTip k="pricing.min_margin" />}
            error={fieldError(run.error, 'minMarginBps')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={f.minMargin}
                onChange={(e) => setF({ ...f, minMargin: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.pricing.minMarginMode')}>
            {(p) => (
              <Select
                {...p}
                value={f.minMarginMode}
                disabled={!f.minMargin.trim()}
                onChange={(e) => setF({ ...f, minMarginMode: e.target.value as 'warn' | 'block' })}
              >
                <option value="warn">{t('admin.pricing.minMarginModes.warn')}</option>
                <option value="block">{t('admin.pricing.minMarginModes.block')}</option>
              </Select>
            )}
          </Field>
        </div>
      )}
      <Button
        className="mt-3"
        loading={run.isPending}
        disabled={!ready}
        onClick={() => run.mutate(undefined)}
      >
        {t('admin.pricing.previewBulk')}
      </Button>
      {preview && (
        <div className="mt-4 space-y-3 rounded-md border border-line p-4" role="status">
          <p className="text-sm">
            {t('admin.pricing.previewSummary', { affected: preview.affected, skipped: preview.skipped })}
          </p>
          {preview.noCost > 0 && (
            <p className="text-sm text-ink-2">{t('admin.pricing.noCost', { count: preview.noCost })}</p>
          )}
          {preview.lowMargin > 0 && (
            <p className="text-sm text-amber">
              {t('admin.pricing.lowMarginCount', { count: preview.lowMargin })}
            </p>
          )}
          {preview.blocked > 0 && (
            <p className="text-sm text-amber">{t('admin.pricing.blocked', { count: preview.blocked })}</p>
          )}
          {preview.sample.length > 0 && (
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-ink-2">
                  <tr>
                    <th className="py-1 text-start font-medium">{t('admin.products.product')}</th>
                    <th className="py-1 text-end font-medium">{t('admin.pricing.before')}</th>
                    <th className="py-1 text-end font-medium">{t('admin.pricing.after')}</th>
                    {showMargin && <th className="py-1 text-end font-medium">{t('admin.pricing.margin')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((s) => (
                    <tr key={s.variantId} className="border-t border-line">
                      <td className="py-1">
                        {s.label} <span className="text-ink-3">{s.sku}</span>
                      </td>
                      <td className="py-1 text-end tabular">{money(s.before, preview.currency)}</td>
                      <td className="py-1 text-end tabular font-medium">
                        {money(s.after, preview.currency)}
                      </td>
                      {showMargin && (
                        <td className="py-1 text-end tabular">
                          {s.marginBps === null ? '—' : bpsLabel(s.marginBps)}
                          {s.lowMargin && (
                            <Badge tone="rose" className="ms-1">
                              {t('admin.pricing.lowMargin')}
                            </Badge>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {can('pricing.bulk.apply') && preview.affected > 0 && (
            <div className="flex items-center gap-1">
              <ConfirmButton
                variant="primary"
                size="md"
                message={t('admin.pricing.confirmApply', { count: preview.affected })}
                onConfirm={() => apply.mutateAsync(preview.id)}
              >
                {t('admin.pricing.applyBulk')}
              </ConfirmButton>
              <HelpTip k="pricing.bulk_apply" />
            </div>
          )}
        </div>
      )}
      {history.data && history.data.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-sm font-medium">{t('admin.pricing.history')}</p>
          <ul className="divide-y divide-line rounded-md border border-line">
            {history.data.map((h) => (
              <li key={h.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
                <span className="flex-1">
                  {formatDate(h.createdAt, true)} ·{' '}
                  {t('admin.pricing.affectedCount', { count: h.affectedCount })}
                </span>
                <Badge tone={h.status === 'applied' ? 'moss' : h.status === 'reverted' ? 'slate' : 'neutral'}>
                  {t(`admin.pricing.bulkStatus.${h.status}`)}
                </Badge>
                {h.status === 'applied' && can('pricing.bulk.apply') && (
                  <div className="flex items-center gap-1">
                    <ConfirmButton
                      message={t('admin.pricing.confirmRevert')}
                      onConfirm={() => revert.mutateAsync(h.id)}
                    >
                      {t('admin.pricing.revert')}
                    </ConfirmButton>
                    <HelpTip k="pricing.bulk_revert" />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Kuponlar
// ---------------------------------------------------------------------------

export function DiscountsPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('pricing.price.write');
  const list = useQuery({
    queryKey: ['discounts'],
    queryFn: () => api.get<Discount[]>('/v1/admin/discounts'),
  });
  const [open, setOpen] = useState(false);
  const toggle = useApiMutation(
    (d: Discount) =>
      api.patch(
        `/v1/admin/discounts/${d.id}`,
        { status: d.status === 'active' ? 'inactive' : 'active' },
        { ifMatch: d.version },
      ),
    { invalidate: [['discounts']], success: t('common.saved') },
  );
  return (
    <PageFrame
      helpKey="page.discounts"
      actions={
        canWrite && (
          <Button variant="primary" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.discounts.create')}
          </Button>
        )
      }
    >
      {list.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !list.data?.length ? (
        <EmptyState title={t('admin.discounts.empty')} description={t('admin.discounts.emptyLead')} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {list.data.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  <code className="rounded-sm bg-surface-2 px-1.5 py-0.5">{d.code}</code> {d.name}
                </p>
                <p className="text-sm text-ink-3">
                  {d.type === 'percentage' ? bpsLabel(d.value) : money(d.value, d.currency)}
                  {d.minSubtotalMinor
                    ? ` · ${t('admin.discounts.min', { amount: money(d.minSubtotalMinor, d.currency ?? 'TRY') })}`
                    : ''}
                  {` · ${t('admin.discounts.used', { used: d.usedCount, limit: d.usageLimit ?? '∞' })}`}
                  {d.endsAt ? ` · ${t('admin.discounts.until', { date: formatDate(d.endsAt) })}` : ''}
                </p>
                {d.companies.length > 0 && (
                  <p className="text-sm text-ink-2">
                    {t('admin.dealerCampaigns.couponFor', {
                      names: d.companies.map((c) => c.legalName).join(', '),
                    })}
                  </p>
                )}
              </div>
              <Badge tone={d.status === 'active' ? 'moss' : 'slate'}>
                {t(`admin.pricing.listStatus.${d.status}`)}
              </Badge>
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => toggle.mutate(d)}>
                  {d.status === 'active' ? t('admin.pricing.deactivate') : t('admin.pricing.activate')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && <DiscountDialog open={open} onOpenChange={setOpen} />}
    </PageFrame>
  );
}

function DiscountDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const t = useT();
  const groups = useGroups();
  const [f, setF] = useState({
    name: '',
    code: '',
    type: 'percentage' as 'percentage' | 'fixed_amount',
    value: '',
    minSubtotal: '',
    usageLimit: '',
    endsAt: '',
    groupIds: [] as string[],
    companies: [] as PickedCompany[],
  });
  const { can } = usePermissions();
  const create = useApiMutation(
    () =>
      api.post('/v1/admin/discounts', {
        name: f.name,
        code: f.code,
        type: f.type,
        value: f.type === 'percentage' ? (percentToBps(f.value) ?? 0) : (toMinor(f.value, 'TRY') ?? 0),
        currency: f.type === 'fixed_amount' ? 'TRY' : null,
        minSubtotalMinor: f.minSubtotal ? toMinor(f.minSubtotal, 'TRY') : null,
        usageLimit: f.usageLimit ? Number(f.usageLimit) : null,
        endsAt: f.endsAt ? new Date(`${f.endsAt}T23:59:59`).toISOString() : null,
        customerGroupIds: f.groupIds.length ? f.groupIds : null,
        companyIds: f.companies.length ? f.companies.map((c) => c.id) : null,
        status: 'active',
      }),
    {
      invalidate: [['discounts']],
      success: t('common.saved'),
      onSuccess: () => onOpenChange(false),
      silentFieldErrors: true,
    },
  );
  const err = (k: string) => fieldError(create.error, k);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={t('admin.discounts.create')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={create.isPending} onClick={() => create.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.discounts.name')} required error={err('name')}>
          {(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        </Field>
        <Field
          label={t('admin.discounts.code')}
          required
          error={
            err('code') ?? (create.error?.code === 'discount_code_taken' ? create.error.title : undefined)
          }
          help={<HelpTip k="discounts.code" />}
        >
          {(p) => (
            <Input
              {...p}
              value={f.code}
              onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
            />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('admin.discounts.type')}>
            {(p) => (
              <Select
                {...p}
                value={f.type}
                onChange={(e) => setF({ ...f, type: e.target.value as typeof f.type })}
              >
                <option value="percentage">{t('admin.discounts.types.percentage')}</option>
                <option value="fixed_amount">{t('admin.discounts.types.fixed_amount')}</option>
              </Select>
            )}
          </Field>
          <Field
            label={f.type === 'percentage' ? t('admin.pricing.percent') : t('admin.pricing.amount')}
            required
            error={err('value')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={f.value}
                onChange={(e) => setF({ ...f, value: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.discounts.minSubtotal')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="discounts.min_subtotal" />}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={f.minSubtotal}
                onChange={(e) => setF({ ...f, minSubtotal: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.discounts.usageLimit')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="discounts.usage_limit" />}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={f.usageLimit}
                onChange={(e) => setF({ ...f, usageLimit: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.discounts.endsAt')}
            optionalLabel={t('common.optional')}
            error={err('endsAt')}
          >
            {(p) => (
              <Input
                {...p}
                type="date"
                value={f.endsAt}
                onChange={(e) => setF({ ...f, endsAt: e.target.value })}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('admin.discounts.groups')}
          help={<HelpTip k="discounts.groups" />}
          description={t('admin.discounts.groupsHint')}
        >
          {() => (
            <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
              {groups.data?.map((g) => (
                <label key={g.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="accent-[var(--zt-pine)]"
                    checked={f.groupIds.includes(g.id)}
                    onChange={(e) =>
                      setF({
                        ...f,
                        groupIds: e.target.checked
                          ? [...f.groupIds, g.id]
                          : f.groupIds.filter((x) => x !== g.id),
                      })
                    }
                  />
                  {g.name}
                </label>
              ))}
            </div>
          )}
        </Field>
        {can('b2b.dealer.read') && (
          <CompanyMultiPicker
            label={t('admin.dealerCampaigns.companies')}
            help={<HelpTip k="discounts.companies" />}
            value={f.companies}
            onChange={(v) => setF({ ...f, companies: v })}
            error={err('companyIds')}
          />
        )}
      </div>
    </Dialog>
  );
}
